import { Component, Input, OnInit, Output, EventEmitter, OnDestroy, AfterViewInit, ElementRef, ViewChild, NgZone } from '@angular/core';
import { LandingsService } from 'src/app/shared/service/landings.service';
import { LandingPagesService } from 'src/app/shared/service/landing-pages.service';
import { BuilderApiService } from 'src/app/shared/service/builder-api.service';
import { APIService } from 'src/app/shared/service/apiService.service';
import { ProductsService } from 'src/app/shared/service/product.service';
import { CdkDragDrop, moveItemInArray, transferArrayItem, DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { lastValueFrom } from 'rxjs';

interface ComponenteBuilder {
  id: string;
  tipo: 'titulo' | 'texto' | 'imagen' | 'redes' | 'boton' | 'espaciador' | 'fila' | 'contador' | 'perfil' | 'banner' | 'descuento';
  contenido: any;
  estilos: any;
  columnas?: ComponenteBuilder[][]; // Array de arrays para múltiples componentes por columna
}

interface Landing4Settings {
  backgroundColor: string;
  moduleSpacing: string;
  fontFamily: string;
}

@Component({
  selector: 'app-landing4',
  standalone: true,
  imports: [CommonModule, DragDropModule, FormsModule],
  templateUrl: './landing4.component.html',
  styleUrls: ['./landing4.component.scss']
})
export class Landing4Component implements OnInit, OnDestroy, AfterViewInit {
  @Input() gancho: string = '';
  @Input() propuesta: string = '';
  @Input() deseo: string = '';
  @Input() imagen: string = '';
  @Input() queObtendras: any[] = [];
  @Input() imgObtendras: string = '';
  @Input() somos: string = '';
  @Input() recursos: string = '';
  @Input() preguntas: string[] = [];
  @Input() respuestas: string[] = [];
  @Input() color1: string = '#3498db';
  @Input() color2: string = '#2ecc71';
  @Input() logo: string = '';
  @Input() tipografia: string = 'Arial';
  @Input() precioOriginal: string = '500';
  @Input() precioDescuento: string = '399';
  @Input() productId: number | null = null;
  @Input() landingPageId: number | null = null;

  @Output() componentesChange = new EventEmitter<string[]>();
  @Output() finalizarEvent = new EventEmitter<void>();

  // Tipografías disponibles
  tipografiasDisponibles = [
    { value: "'Work Sans', sans-serif", label: 'Work Sans' },
    { value: "'Nunito', sans-serif", label: 'Nunito' },
    { value: "'Roboto', sans-serif", label: 'Roboto' },
    { value: "'Poppins', sans-serif", label: 'Poppins' },
    { value: "'Playfair Display', serif", label: 'Playfair Display' },
    { value: "'Montserrat', sans-serif", label: 'Montserrat' },
    { value: "'Lato', sans-serif", label: 'Lato' },
    { value: "'Open Sans', sans-serif", label: 'Open Sans' },
    { value: 'Arial, sans-serif', label: 'Arial' },
    { value: 'Georgia, serif', label: 'Georgia' }
  ];

  // Componentes disponibles para arrastrar
  componentesDisponibles = [
    { tipo: 'titulo', icono: 'fas fa-heading', nombre: 'Título' },
    { tipo: 'texto', icono: 'fas fa-align-left', nombre: 'Texto' },
    { tipo: 'imagen', icono: 'fas fa-image', nombre: 'Imagen' },
    { tipo: 'redes', icono: 'fas fa-share-alt', nombre: 'Redes Sociales' },
    { tipo: 'boton', icono: 'fas fa-mouse-pointer', nombre: 'Botón' },
    { tipo: 'fila', icono: 'fas fa-columns', nombre: 'Fila (2 Columnas)' },
    { tipo: 'espaciador', icono: 'fas fa-arrows-alt-v', nombre: 'Espaciador' },
    { tipo: 'contador', icono: 'fas fa-hourglass-half', nombre: 'Contador' },
    { tipo: 'perfil', icono: 'fas fa-id-badge', nombre: 'Perfil' },
    { tipo: 'banner', icono: 'fas fa-rectangle-ad', nombre: 'Banner' },
    { tipo: 'descuento', icono: 'fas fa-tags', nombre: 'Descuento' }
  ];

  // Componentes agregados al canvas
  componentesCanvas: ComponenteBuilder[] = [];
  
  // Componente seleccionado para editar
  componenteSeleccionado: ComponenteBuilder | null = null;

  modoEdicion = true; // Modo edición vs preview
  mostrarPanelPropiedades = false;
  sidebarColapsada = false;
  propertiesColapsada = false;
  toolbarColapsada = false;
  columnaSeleccionada: { filaId: string, columnaIndex: number } | null = null;
  columnaExpandida: number | null = null;
  componenteColumnaSeleccionado: { filaId: string, columnaIndex: number, componenteIndex: number } | null = null;

  // Configuración global
  settings: Landing4Settings = {
    backgroundColor: '#ffffff',
    moduleSpacing: '30px',
    fontFamily: "'Work Sans', sans-serif"
  };

  // Cache de datos de productos
  productDataCache: { [id: number]: any } = {};
  loadingProducts: { [id: number]: boolean } = {};

  // Timers para contadores
  private contadorIntervals: { [id: string]: any } = {};
  contadorValores: { [id: string]: string } = {};

  @ViewChild('builderContainer') builderContainerRef?: ElementRef<HTMLDivElement>;
  private contentResizeObs?: ResizeObserver;
  isGuardandoBuilder: boolean = false;
  private alerta(msg: string): void { try { alert(msg); } catch { console.warn(msg); } }

  constructor(
    private api: APIService,
    private ngZone: NgZone,
    private landingsService: LandingsService,
    private landingPagesService: LandingPagesService,
    private builderApi: BuilderApiService,
    private productsService: ProductsService,
  ) {}

  ngOnInit(): void {
    // Inicializar con componentes de ejemplo si es necesario
    this.cargarDatosGuardados();
    this.refreshContadores();
    this.refreshPerfiles();
  }

  ngOnDestroy(): void {
    this.clearAllContadores();
    if (this.contentResizeObs) {
      this.contentResizeObs.disconnect();
      this.contentResizeObs = undefined;
    }
  }

  ngAfterViewInit(): void {
    // Observar el contenedor de contenido del mat-stepper para sincronizar alturas
    const builderEl = this.builderContainerRef?.nativeElement;
    if (!builderEl) return;
    // Priorizar el ancestro con class="col-12" solicitado; fallback al contenedor del stepper
    let hostContent = builderEl.closest('.col-12') as HTMLElement | null;
    if (!hostContent) {
      hostContent = builderEl.closest('.mat-horizontal-content-container') as HTMLElement | null;
    }
    const applyHeight = (h: number) => {
      // Altura del contenido total; los sidebars descontarán la toolbar y un margen de 30px en SCSS
      builderEl.style.setProperty('--content-h', `${Math.max(0, Math.round(h))}px`);
    };
    if (hostContent) {
      // Inicializar con medida actual
      try { applyHeight(hostContent.getBoundingClientRect().height); } catch {}
      // Observar cambios reactivos de tamaño
      this.ngZone.runOutsideAngular(() => {
        this.contentResizeObs = new ResizeObserver((entries) => {
          for (const entry of entries) {
            const cr = entry.contentRect;
            applyHeight(cr.height);
          }
        });
        this.contentResizeObs.observe(hostContent);
      });
    } else {
      // Fallback: usar altura de viewport si no se encuentra el contenedor
      applyHeight(window.innerHeight);
    }
  }

  // TrackBy functions para optimizar el rendimiento del *ngFor
  trackByComponenteId(index: number, componente: ComponenteBuilder): string {
    return componente.id;
  }

  trackByColumnIndex(index: number): number {
    return index;
  }

  private getStorageKey(): string {
    if (this.landingPageId) return `landing4_config_lp_${this.landingPageId}`;
    if (this.productId) return `landing4_config_${this.productId}`;
    return 'landing4_config';
  }

  private aplicarPayloadBuilder(data: any): void {
    if (!data) return;
    this.componentesCanvas = data.componentes || [];
    this.settings = { ...this.settings, ...(data.settings || {}) };
    if (!this.settings.moduleSpacing) this.settings.moduleSpacing = '30px';
    this.normalizeConfig();
  }

  cargarDatosGuardados(): void {
    if (this.landingPageId) {
      this.landingPagesService.getById(this.landingPageId).subscribe({
        next: (lp) => {
          const data = lp.data;
          if (data && (Array.isArray(data.componentes) || data.settings)) {
            this.aplicarPayloadBuilder(data);
          } else {
            this.cargarDesdeLocalStorage();
          }
        },
        error: () => this.cargarDesdeLocalStorage(),
      });
      return;
    }

    if (this.productId) {
      this.landingsService.getLandingPage4(this.productId).subscribe({
        next: (response: any) => {
          if (response.success && response.data) {
            this.aplicarPayloadBuilder(response.data);
          } else {
            this.cargarDesdeLocalStorage();
          }
        },
        error: () => this.cargarDesdeLocalStorage(),
      });
      return;
    }

    this.cargarDesdeLocalStorage();
  }

  private cargarDesdeLocalStorage(): void {
    const storageKey = this.getStorageKey();
    const savedConfig = localStorage.getItem(storageKey);
    if (savedConfig) {
      try {
        const parsed = JSON.parse(savedConfig);
        if (Array.isArray(parsed)) {
          // Formato antiguo: solo componentes
          this.componentesCanvas = parsed;
        } else if (parsed && typeof parsed === 'object') {
          this.componentesCanvas = parsed.componentes || [];
          this.settings = { ...this.settings, ...(parsed.settings || {}) };
          if (!this.settings.moduleSpacing) this.settings.moduleSpacing = '30px';
          this.normalizeConfig();
        }
      } catch {
        this.componentesCanvas = [];
      }
    } else {
      // Configuración inicial por defecto
      this.componentesCanvas = [
        {
          id: this.generarId(),
          tipo: 'titulo',
          contenido: { texto: this.gancho || 'Título Principal' },
          estilos: { fontSize: '32px', color: this.color1, textAlign: 'center', fontWeight: 'bold' }
        },
        {
          id: this.generarId(),
          tipo: 'texto',
          contenido: { texto: this.propuesta || 'Propuesta de valor...' },
          estilos: { fontSize: '16px', color: '#333', textAlign: 'center' }
        }
      ];
    }
  }

  guardarConfiguracion(): void {
    const data = { componentes: this.componentesCanvas, settings: this.settings };
    localStorage.setItem(this.getStorageKey(), JSON.stringify(data));
  }

  // Drag and drop desde la barra de herramientas y reordenamiento
  onDropComponente(event: CdkDragDrop<any>): void {
    if (event.previousContainer === event.container) {
      // Reordenar dentro del canvas
      moveItemInArray(this.componentesCanvas, event.previousIndex, event.currentIndex);
    } else {
      // Arrastrar desde el sidebar al canvas o desde una columna al canvas
      const data = event.item.data;
      
      if (typeof data === 'string') {
        // Es un tipo de componente desde el sidebar - crear nuevo
        const nuevoComponente = this.crearComponente(data);
        this.componentesCanvas.splice(event.currentIndex, 0, nuevoComponente);
        this.emitirComponentesActivos();
      } else if (data && typeof data === 'object' && 'id' in data) {
        // Es un componente que se mueve desde una columna al canvas
        // Buscar y remover de la columna origen
        let encontrado = false;
        this.componentesCanvas.forEach(comp => {
          if (comp.tipo === 'fila' && comp.columnas) {
            comp.columnas.forEach((col) => {
              const idx = col.findIndex(c => c.id === data.id);
              if (idx !== -1) {
                col.splice(idx, 1);
                encontrado = true;
              }
            });
          }
        });
        
        if (encontrado) {
          this.componentesCanvas.splice(event.currentIndex, 0, data);
          this.emitirComponentesActivos();
        }
      }
    }
    this.guardarConfiguracion();
    this.refreshContadores();
  }

  agregarComponente(tipo: string): void {
    const nuevoComponente = this.crearComponente(tipo);
    this.componentesCanvas.push(nuevoComponente);
    this.guardarConfiguracion();
    this.emitirComponentesActivos();
    if (nuevoComponente.tipo === 'contador') {
      this.startContador(nuevoComponente.id, nuevoComponente.contenido?.targetDate);
    }
    if (nuevoComponente.tipo === 'perfil') {
      this.hidratarPerfil(nuevoComponente);
    }
  }

  crearComponente(tipo: string): ComponenteBuilder {
    const id = this.generarId();
    
    switch (tipo) {
      case 'titulo':
        return {
          id,
          tipo: 'titulo',
          contenido: { texto: 'Nuevo Título' },
          estilos: { fontSize: '28px', color: this.color1, textAlign: 'left', fontWeight: 'bold', fontFamily: this.settings.fontFamily, borderStyle: 'none', borderWidth: '1px', borderColor: '#cccccc' }
        };
      case 'texto':
        return {
          id,
          tipo: 'texto',
          contenido: { texto: 'Escribe tu texto aquí...' },
          estilos: { fontSize: '16px', color: '#333', textAlign: 'left', fontFamily: this.settings.fontFamily, borderStyle: 'none', borderWidth: '1px', borderColor: '#cccccc' }
        };
      case 'imagen':
        return {
          id,
          tipo: 'imagen',
          contenido: { url: 'https://via.placeholder.com/600x400', alt: 'Imagen' },
          estilos: { width: '100%', maxWidth: '600px', margin: 'auto', textAlign: 'center', fontFamily: this.settings.fontFamily, borderStyle: 'none', borderWidth: '1px', borderColor: '#cccccc' }
        };
      case 'redes':
        return {
          id,
          tipo: 'redes',
          contenido: {
            facebook: '',
            twitter: '',
            instagram: '',
            linkedin: '',
            youtube: '',
            tiktok: ''
          },
          estilos: { textAlign: 'center', iconSize: '32px', fontFamily: this.settings.fontFamily, borderStyle: 'none', borderWidth: '1px', borderColor: '#cccccc' }
        };
      case 'boton':
        return {
          id,
          tipo: 'boton',
          contenido: { texto: 'Comprar Ahora', url: '#' },
          estilos: { 
            backgroundColor: this.color1, 
            color: '#fff',
            textColor: '#fff',
            padding: '12px 30px',
            borderRadius: '5px',
            textAlign: 'center',
            fontFamily: this.settings.fontFamily,
            borderStyle: 'none',
            borderWidth: '1px',
            borderColor: '#cccccc'
          }
        };
      case 'espaciador':
        return {
          id,
          tipo: 'espaciador',
          contenido: {},
          estilos: { height: '40px', fontFamily: this.settings.fontFamily, borderStyle: 'none', borderWidth: '1px', borderColor: '#cccccc' }
        };
      case 'contador':
        // Por defecto: 24h desde ahora
        const target = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
        return {
          id,
          tipo: 'contador',
          contenido: { targetDate: target },
          estilos: { color: '#e74c3c', fontSize: '24px', textAlign: 'center', fontWeight: 'bold', backgroundColor: 'transparent', padding: '10px', fontFamily: this.settings.fontFamily, borderStyle: 'none', borderWidth: '1px', borderColor: '#cccccc' }
        };
      case 'fila':
        return {
          id,
          tipo: 'fila',
          contenido: { numColumnas: 2 },
          estilos: { 
            gap: '20px',
            colWidths: ['1', '1'],
            colBgColors: ['', ''],
            colBorderStyles: ['none','none'],
            colBorderWidths: ['1px','1px'],
            colBorderColors: ['#cccccc','#cccccc'],
            fontFamily: this.settings.fontFamily
          },
          columnas: [
            [],
            []
          ]
        };
      case 'perfil':
        return {
          id,
          tipo: 'perfil',
          contenido: { nombre: 'Nombre Apellido', rol: 'Rol/Profesión', bio: 'Breve bio o descripción', avatarUrl: '' },
          estilos: { textAlign: 'left', color: '#2c3e50', fontFamily: this.settings.fontFamily, borderStyle: 'none', borderWidth: '1px', borderColor: '#cccccc' }
        };
      case 'banner':
        return {
          id,
          tipo: 'banner',
          contenido: {
            titulo: 'Oferta Especial',
            subtitulo: 'Aprovecha esta increíble oportunidad',
            imagenFondo: 'https://via.placeholder.com/1200x400',
            textoBoton: 'Ver Oferta',
            urlBoton: '#'
          },
          estilos: {
            overlayColor: 'rgba(0, 0, 0, 0.4)',
            colorTitulo: '#ffffff',
            colorSubtitulo: '#f0f0f0',
            textAlign: 'center',
            padding: '80px 20px',
            fontFamily: this.settings.fontFamily,
            borderStyle: 'none',
            borderWidth: '1px',
            borderColor: '#cccccc'
          }
        };
      case 'descuento':
        const targetDescuento = new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString();
        return {
          id,
          tipo: 'descuento',
          contenido: {
            precioOriginal: this.precioOriginal || '500',
            precioDescuento: this.precioDescuento || '399',
            moneda: 'MXN',
            textoDescuento: '¡Precio Especial!',
            mostrarContador: true,
            targetDate: targetDescuento,
            porcentajeDescuento: 0
          },
          estilos: {
            backgroundColor: '#fff3cd',
            colorTexto: '#856404',
            colorPrecioOriginal: '#6c757d',
            colorPrecioDescuento: '#28a745',
            padding: '30px',
            borderRadius: '10px',
            textAlign: 'center',
            fontFamily: this.settings.fontFamily,
            borderStyle: 'solid',
            borderWidth: '2px',
            borderColor: '#ffc107'
          }
        };
      default:
        return {
          id,
          tipo: 'texto',
          contenido: { texto: '' },
          estilos: {}
        };
    }
  }

  generarId(): string {
    return `comp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  seleccionarComponente(componente: ComponenteBuilder): void {
    this.componenteSeleccionado = componente;
    this.mostrarPanelPropiedades = true;
    this.propertiesColapsada = false; // Expandir automáticamente al seleccionar
    if (componente.tipo === 'fila') {
      const estilos: any = componente.estilos || (componente.estilos = {});
      const cols = componente.columnas || [];
      if (!Array.isArray(estilos.colBgColors)) estilos.colBgColors = [];
      if (estilos.colBgColors.length < cols.length) {
        for (let i = estilos.colBgColors.length; i < cols.length; i++) estilos.colBgColors[i] = '';
      } else if (estilos.colBgColors.length > cols.length) {
        estilos.colBgColors = estilos.colBgColors.slice(0, cols.length);
      }
    }
  }

  eliminarComponente(id: string): void {
    this.componentesCanvas = this.componentesCanvas.filter(c => c.id !== id);
    if (this.componenteSeleccionado?.id === id) {
      this.componenteSeleccionado = null;
      this.mostrarPanelPropiedades = false;
    }
    this.clearContador(id);
    this.guardarConfiguracion();
    this.emitirComponentesActivos();
  }

  duplicarComponente(componente: ComponenteBuilder): void {
    const duplicado = JSON.parse(JSON.stringify(componente));
    duplicado.id = this.generarId();
    const index = this.componentesCanvas.findIndex(c => c.id === componente.id);
    this.componentesCanvas.splice(index + 1, 0, duplicado);
    this.guardarConfiguracion();
    this.emitirComponentesActivos();
    if (duplicado.tipo === 'contador') {
      this.startContador(duplicado.id, duplicado.contenido?.targetDate);
    }
  }

  emitirComponentesActivos(): void {
    const tiposActivos = [...new Set(this.componentesCanvas.map(c => c.tipo))];
    this.componentesChange.emit(tiposActivos);
  }

  actualizarContenido(propiedad: string, valor: any): void {
    if (this.componenteSeleccionado) {
      this.componenteSeleccionado.contenido[propiedad] = valor;
      this.guardarConfiguracion();
    }
  }

  actualizarEstilo(propiedad: string, valor: any): void {
    if (this.componenteSeleccionado) {
      this.componenteSeleccionado.estilos[propiedad] = valor;
      this.guardarConfiguracion();
    }
  }

  // ================= Contadores =================
  private formatDiff(ms: number): string {
    if (ms <= 0) return '00:00:00';
    const totalSec = Math.floor(ms / 1000);
    const d = Math.floor(totalSec / 86400);
    const h = Math.floor((totalSec % 86400) / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    const hh = (h + d * 24).toString().padStart(2, '0');
    return `${hh}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }

  private startContador(id: string, targetISO: string): void {
    this.clearContador(id);
    const update = () => {
      const target = new Date(targetISO).getTime();
      const now = Date.now();
      const diff = target - now;
      this.contadorValores[id] = this.formatDiff(diff);
    };
    update();
    this.contadorIntervals[id] = setInterval(update, 1000);
  }

  private clearContador(id: string): void {
    if (this.contadorIntervals[id]) {
      clearInterval(this.contadorIntervals[id]);
      delete this.contadorIntervals[id];
    }
    delete this.contadorValores[id];
  }

  private clearAllContadores(): void {
    Object.keys(this.contadorIntervals).forEach(id => this.clearContador(id));
  }

  private refreshContadores(): void {
    this.clearAllContadores();
    const visit = (comp: ComponenteBuilder) => {
      if (comp.tipo === 'contador') {
        this.startContador(comp.id, comp.contenido?.targetDate);
      } else if (comp.tipo === 'fila' && comp.columnas) {
        comp.columnas.forEach(col => col.forEach(visit));
      }
    };
    this.componentesCanvas.forEach(visit);
  }

  actualizarTargetContador(componente: ComponenteBuilder, iso: string): void {
    componente.contenido.targetDate = iso;
    this.startContador(componente.id, iso);
    this.guardarConfiguracion();
  }

  onDateTimeChange(componente: ComponenteBuilder, value: string): void {
    if (!value) return;
    try {
      const iso = new Date(value).toISOString();
      this.actualizarTargetContador(componente, iso);
    } catch {}
  }

  // ================= Settings =================
  actualizarBackgroundColor(color: string): void {
    this.settings.backgroundColor = color;
    this.guardarConfiguracion();
  }

  toggleModoEdicion(): void {
    this.modoEdicion = !this.modoEdicion;
    if (!this.modoEdicion) {
      this.mostrarPanelPropiedades = false;
      this.componenteSeleccionado = null;
    }
  }

  finalizarLanding(): void {
    this.guardarConfiguracion();
    this.finalizarEvent.emit();
  }

  toggleSidebar(): void {
    this.sidebarColapsada = !this.sidebarColapsada;
  }

  toggleProperties(): void {
    this.propertiesColapsada = !this.propertiesColapsada;
  }

  toggleToolbar(): void {
    this.toolbarColapsada = !this.toolbarColapsada;
  }

  agregarComponenteAColumna(filaId: string, columnaIndex: number, tipo: string): void {
    const fila = this.componentesCanvas.find(c => c.id === filaId);
    if (fila && fila.columnas) {
      const nuevoComponente = this.crearComponente(tipo);
      if (!fila.columnas[columnaIndex]) {
        fila.columnas[columnaIndex] = [];
      }
      fila.columnas[columnaIndex].push(nuevoComponente);
      this.guardarConfiguracion();
      this.emitirComponentesActivos();
      if (nuevoComponente.tipo === 'contador') {
        this.startContador(nuevoComponente.id, nuevoComponente.contenido?.targetDate);
      }
      if (nuevoComponente.tipo === 'perfil') {
        this.hidratarPerfil(nuevoComponente);
      }
      if (nuevoComponente.tipo === 'descuento') {
        if (nuevoComponente.contenido.mostrarContador) {
          this.startContador(nuevoComponente.id, nuevoComponente.contenido.targetDate);
        }
      }
    }
  }

  seleccionarColumna(filaId: string, columnaIndex: number): void {
    this.columnaSeleccionada = { filaId, columnaIndex };
    this.columnaExpandida = columnaIndex;
  }

  seleccionarColumnaYFila(fila: ComponenteBuilder, columnaIndex: number): void {
    this.seleccionarColumna(fila.id, columnaIndex);
    this.seleccionarComponente(fila);
  }

  toggleColumnaExpanded(colIdx: number): void {
    this.columnaExpandida = this.columnaExpandida === colIdx ? null : colIdx;
  }

  seleccionarComponenteColumna(filaId: string, columnaIndex: number, componenteIndex: number, componente: ComponenteBuilder): void {
    this.componenteColumnaSeleccionado = { filaId, columnaIndex, componenteIndex };
  }

  moverComponenteColumna(filaId: string, columnaIndex: number, componenteIndex: number, direccion: 'up' | 'down'): void {
    const fila = this.componentesCanvas.find(c => c.id === filaId);
    if (!fila || !fila.columnas || !fila.columnas[columnaIndex]) return;

    const columna = fila.columnas[columnaIndex];
    const newIndex = direccion === 'up' ? componenteIndex - 1 : componenteIndex + 1;

    if (newIndex >= 0 && newIndex < columna.length) {
      const temp = columna[componenteIndex];
      columna[componenteIndex] = columna[newIndex];
      columna[newIndex] = temp;
      this.guardarConfiguracion();
    }
  }

  getTipoIcon(tipo: string): string {
    const iconos: { [key: string]: string } = {
      'titulo': 'heading',
      'texto': 'align-left',
      'imagen': 'image',
      'boton': 'mouse-pointer',
      'redes': 'share-alt',
      'espaciador': 'arrows-alt-v',
      'contador': 'hourglass-half',
      'perfil': 'id-badge',
      'banner': 'rectangle-ad',
      'descuento': 'tags'
    };
    return iconos[tipo] || 'cube';
  }

  getTipoNombre(tipo: string): string {
    const nombres: { [key: string]: string } = {
      'titulo': 'Título',
      'texto': 'Texto',
      'imagen': 'Imagen',
      'boton': 'Botón',
      'redes': 'Redes Sociales',
      'espaciador': 'Espaciador',
      'contador': 'Contador',
      'perfil': 'Perfil',
      'banner': 'Banner',
      'descuento': 'Descuento'
    };
    return nombres[tipo] || tipo;
  }

  onDropEnColumna(event: CdkDragDrop<ComponenteBuilder[]>, filaId: string, columnaIndex: number): void {
    const fila = this.componentesCanvas.find(c => c.id === filaId);
    if (!fila || !fila.columnas) return;

    // Asegurar que la columna existe
    if (!fila.columnas[columnaIndex]) {
      fila.columnas[columnaIndex] = [];
    }

    if (event.previousContainer === event.container) {
      // Reordenar dentro de la misma columna
      moveItemInArray(fila.columnas[columnaIndex], event.previousIndex, event.currentIndex);
    } else {
      // Arrastrar desde sidebar u otra ubicación
      const data = event.item.data;
      
      if (typeof data === 'string') {
        // Es un tipo de componente desde el sidebar - crear nuevo componente
        const nuevoComponente = this.crearComponente(data);
        fila.columnas[columnaIndex].splice(event.currentIndex, 0, nuevoComponente);
        this.emitirComponentesActivos();
      } else if (data && typeof data === 'object' && 'id' in data) {
        // Es un componente existente que se mueve de otra columna o del canvas
        // Verificar si viene del canvas principal
        const indexEnCanvas = this.componentesCanvas.findIndex(c => c.id === data.id);
        
        if (indexEnCanvas !== -1) {
          // Remover del canvas principal
          this.componentesCanvas.splice(indexEnCanvas, 1);
          fila.columnas[columnaIndex].splice(event.currentIndex, 0, data);
        } else {
          // Buscar en otras columnas para mover
          let encontrado = false;
          this.componentesCanvas.forEach(comp => {
            if (comp.tipo === 'fila' && comp.columnas) {
              comp.columnas.forEach((col, colIdx) => {
                const idx = col.findIndex(c => c.id === data.id);
                if (idx !== -1) {
                  col.splice(idx, 1);
                  encontrado = true;
                }
              });
            }
          });
          
          if (encontrado) {
            fila.columnas[columnaIndex].splice(event.currentIndex, 0, data);
          }
        }
        this.emitirComponentesActivos();
      }
    }
    this.guardarConfiguracion();
  }

  actualizarNumeroColumnas(fila: ComponenteBuilder, numColumnas: number): void {
    if (!fila.columnas) {
      fila.columnas = [];
    }

    const columnasActuales = fila.columnas.length;
    const estilos: any = fila.estilos || (fila.estilos = {});
    estilos.colBgColors = Array.isArray(estilos.colBgColors) ? estilos.colBgColors : [];
    estilos.colWidths = Array.isArray(estilos.colWidths) ? estilos.colWidths : [];
    
    if (numColumnas > columnasActuales) {
      // Agregar nuevas columnas vacías
      for (let i = columnasActuales; i < numColumnas; i++) {
        fila.columnas.push([]);
        estilos.colBgColors[i] = estilos.colBgColors[i] || '';
        estilos.colWidths[i] = estilos.colWidths[i] || '1';
      }
    } else if (numColumnas < columnasActuales) {
      // Remover columnas del final
      fila.columnas = fila.columnas.slice(0, numColumnas);
      estilos.colBgColors = estilos.colBgColors.slice(0, numColumnas);
      estilos.colWidths = estilos.colWidths.slice(0, numColumnas);
    }

    fila.contenido.numColumnas = numColumnas;
    this.guardarConfiguracion();
  }

  eliminarComponenteDeColumna(filaId: string, columnaIndex: number, componenteIndex: number): void {
    const fila = this.componentesCanvas.find(c => c.id === filaId);
    if (fila && fila.columnas && fila.columnas[columnaIndex]) {
      fila.columnas[columnaIndex].splice(componenteIndex, 1);
      this.guardarConfiguracion();
    }
  }

  onImagenSelected(event: any, componente: ComponenteBuilder): void {
    const file = event.target.files[0];
    if (!file) return;
    // Marcar estado de subida y previsualizar con ObjectURL
    try {
      componente.contenido.uploading = true;
      componente.contenido.fileName = file.name;
      try { componente.contenido.url = URL.createObjectURL(file); } catch {}
      const formData = new FormData();
      formData.append('file', file, file.name);
      this.landingsService.uploadImg(formData).subscribe({
        next: (resp: any) => {
          // Backend retorna path, lo almacenamos para usar al guardar
          const path = resp?.path || resp?.filename;
          if (path) {
            // Almacenar el path para el backend
            componente.contenido._uploadedPath = path;
            // Obtener dataUrl para preview
            const [fileType, fileName] = path.includes('/') ? path.split('/') : ['landing', path];
            this.landingsService.getImageAsBase64(fileType, fileName).subscribe({
              next: (imgResp: any) => {
                componente.contenido.url = imgResp?.dataUrl || componente.contenido.url;
                componente.contenido.uploading = false;
                componente.contenido.uploadError = null;
                this.guardarConfiguracion();
              },
              error: () => {
                componente.contenido.uploading = false;
                componente.contenido.uploadError = 'No se pudo recuperar la imagen';
                this.guardarConfiguracion();
              }
            });
          } else {
            componente.contenido.uploading = false;
            componente.contenido.uploadError = 'No se recibió el path de la imagen';
            this.guardarConfiguracion();
          }
        },
        error: () => {
          // Fallback a base64 para no bloquear al usuario
          const reader = new FileReader();
          reader.onload = (e: any) => {
            componente.contenido.url = e.target.result;
            componente.contenido.uploading = false;
            componente.contenido.uploadError = 'No se pudo subir la imagen, usando base64 local.';
            this.guardarConfiguracion();
          };
          try { reader.readAsDataURL(file); } catch {
            componente.contenido.uploading = false;
            componente.contenido.uploadError = 'No se pudo procesar el archivo.';
            this.guardarConfiguracion();
          }
        }
      });
    } catch {
      // Último recurso: intentar leer como base64
      const reader = new FileReader();
      reader.onload = (e: any) => {
        componente.contenido.url = e.target.result;
        componente.contenido.uploading = false;
        componente.contenido.uploadError = 'Subida no disponible, usando base64 local.';
        this.guardarConfiguracion();
      };
      try { reader.readAsDataURL(file); } catch {}
    }
  }

    onAvatarSelected(event: any, componente: ComponenteBuilder): void {
    const file = event.target.files[0];
    if (!file) return;
    try {
      componente.contenido.avatarUploading = true;
      componente.contenido.avatarName = file.name;
      try { componente.contenido.avatarUrl = URL.createObjectURL(file); } catch {}
      const formData = new FormData();
      formData.append('file', file, file.name);
      this.landingsService.uploadImg(formData).subscribe({
        next: (resp: any) => {
          // Backend retorna path, lo almacenamos para usar al guardar
          const path = resp?.path || resp?.filename;
          if (path) {
            // Almacenar el path para el backend
            componente.contenido._avatarUploadedPath = path;
            // Obtener dataUrl para preview
            const [fileType, fileName] = path.includes('/') ? path.split('/') : ['landing', path];
            this.landingsService.getImageAsBase64(fileType, fileName).subscribe({
              next: (imgResp: any) => {
                componente.contenido.avatarUrl = imgResp?.dataUrl || componente.contenido.avatarUrl;
                componente.contenido.avatarUploading = false;
                componente.contenido.avatarUploadError = null;
                this.guardarConfiguracion();
              },
              error: () => {
                componente.contenido.avatarUploading = false;
                componente.contenido.avatarUploadError = 'No se pudo recuperar la imagen';
                this.guardarConfiguracion();
              }
            });
          } else {
            componente.contenido.avatarUploading = false;
            componente.contenido.avatarUploadError = 'No se recibió el path de la imagen';
            this.guardarConfiguracion();
          }
        },
        error: () => {
          const reader = new FileReader();
          reader.onload = (e: any) => {
            componente.contenido.avatarUrl = e.target.result;
            componente.contenido.avatarUploading = false;
            componente.contenido.avatarUploadError = 'No se pudo subir la imagen, usando base64 local.';
            this.guardarConfiguracion();
          };
          try { reader.readAsDataURL(file); } catch {
            componente.contenido.avatarUploading = false;
            componente.contenido.avatarUploadError = 'No se pudo procesar el archivo.';
            this.guardarConfiguracion();
          }
        }
      });
    } catch {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        componente.contenido.avatarUrl = e.target.result;
        componente.contenido.avatarUploading = false;
        componente.contenido.avatarUploadError = 'Subida no disponible, usando base64 local.';
        this.guardarConfiguracion();
      };
      try { reader.readAsDataURL(file); } catch {}
    }
  }

  exportarJSON(): string {
    return JSON.stringify(this.componentesCanvas, null, 2);
  }

  importarJSON(json: string): void {
    try {
      this.componentesCanvas = JSON.parse(json);
      this.normalizeConfig();
      this.guardarConfiguracion();
      this.refreshContadores();
      this.refreshPerfiles();
    } catch (error) {
      console.error('Error al importar JSON:', error);
    }
  }

  getConfiguracion(): any[] {
    return this.componentesCanvas;
  }

  setConfiguracion(config: any[]): void {
    this.componentesCanvas = config;
    this.normalizeConfig();
    this.guardarConfiguracion();
    this.refreshContadores();
    this.refreshPerfiles();
  }

  // ================= Perfil auto-hidratado =================
  private getLocalProfile(): any {
    try {
      const local = JSON.parse(localStorage.getItem('currentUser') || '{}');
      // Recuperar imagen base64 si se guardó aparte en sessionStorage
      const base64 = sessionStorage.getItem('profileImageBase64');
      const image = base64 || local.profileFileUrl || local.imgProfile || null;
      const nombre = local.name || local.fullName || [local.firstName, local.lastName].filter(Boolean).join(' ') || local.username || local.email || 'Usuario';
      const rol = local.role || local.profession || local.title || '';
      const bio = local.bio || '';
      return { nombre, rol, bio, image };
    } catch { return null; }
  }

  private hidratarPerfil(comp: ComponenteBuilder): void {
    if (comp.tipo !== 'perfil') return;
    // 1) Local
    const local = this.getLocalProfile();
    if (local) {
      comp.contenido.nombre = local.nombre || comp.contenido.nombre;
      comp.contenido.rol = local.rol || comp.contenido.rol;
      comp.contenido.bio = local.bio || comp.contenido.bio;
      comp.contenido.avatarUrl = local.image || comp.contenido.avatarUrl;
      this.guardarConfiguracion();
    }
    // 2) Backend
    try {
      this.api.getProfile().subscribe({
        next: (data: any) => {
          if (!data) return;
          const imageUrl = this.resolveProfileImage(data);
          const nombre = data.name || data.fullName || [data.firstName, data.lastName].filter(Boolean).join(' ') || data.username || data.email || comp.contenido.nombre;
          const rol = data.role || data.profession || data.title || comp.contenido.rol;
          const bio = data.bio || comp.contenido.bio;
          comp.contenido = {
            ...comp.contenido,
            nombre,
            rol,
            bio,
            avatarUrl: imageUrl || comp.contenido.avatarUrl
          };
          this.guardarConfiguracion();
        },
        error: () => {}
      });
    } catch {}
  }

  private resolveProfileImage(data: any): string | null {
    // Igual que en header: priorizar URL pública o base64
    let imageUrl = data.profileFileUrl || data.imgProfile || null;
    if (!imageUrl && data.profileImage?.base64) {
      imageUrl = `data:image/jpeg;base64,${data.profileImage.base64}`;
    }
    return imageUrl;
  }

  private refreshPerfiles(): void {
    const visit = (comp: ComponenteBuilder) => {
      if (comp.tipo === 'perfil') this.hidratarPerfil(comp);
      if (comp.tipo === 'fila' && comp.columnas) comp.columnas.forEach(col => col.forEach(visit));
    };
    this.componentesCanvas.forEach(visit);
  }

  // Cargar datos del producto
  loadProductData(productId: number, componente: ComponenteBuilder): void {
    if (!productId || this.loadingProducts[productId]) return;
    
    if (this.productDataCache[productId]) {
      componente.contenido._productData = this.productDataCache[productId];
      return;
    }
    
    this.loadingProducts[productId] = true;
    
    this.productsService.getProduct(String(productId)).subscribe({
      next: (data: any) => {
        this.productDataCache[productId] = data;
        componente.contenido._productData = data;
        this.loadingProducts[productId] = false;
      },
      error: () => {
        this.loadingProducts[productId] = false;
        componente.contenido._productError = 'No se pudo cargar la información del producto';
      }
    });
  }

  // Calcular porcentaje de descuento
  calcularPorcentajeDescuento(original: string | number, descuento: string | number): number {
    const orig = typeof original === 'string' ? parseFloat(original) : original;
    const desc = typeof descuento === 'string' ? parseFloat(descuento) : descuento;
    
    if (!orig || !desc || orig <= 0) return 0;
    
    return Math.round(((orig - desc) / orig) * 100);
  }

  // Detectar si el producto es un ebook
  esEbook(productData: any): boolean {
    if (!productData) return false;
    
    const tieneTopics = productData.product?.some((modulo: any) => 
      Array.isArray(modulo.Topics) && modulo.Topics.length > 0
    );
    
    return !tieneTopics;
  }

  // Validar suma de anchos de columnas
  validarSumaColumnas(colWidths: number[]): { valido: boolean; suma: number } {
    const suma = colWidths.reduce((acc, width) => acc + width, 0);
    return { valido: suma <= 12, suma };
  }

  private normalizeConfig(): void {
    const visit = (comp: ComponenteBuilder) => {
      if (comp.tipo === 'fila') {
        comp.estilos = comp.estilos || {};
        comp.columnas = comp.columnas || [];
        const estilos: any = comp.estilos;
        
        // Colores de fondo por columna
        if (!Array.isArray(estilos.colBgColors)) estilos.colBgColors = [];
        if (estilos.colBgColors.length < comp.columnas.length) {
          for (let i = estilos.colBgColors.length; i < comp.columnas.length; i++) estilos.colBgColors[i] = '';
        } else if (estilos.colBgColors.length > comp.columnas.length) {
          estilos.colBgColors = estilos.colBgColors.slice(0, comp.columnas.length);
        }
        
        // Anchos personalizados por columna (flex-grow)
        if (!Array.isArray(estilos.colWidths)) {
          estilos.colWidths = comp.columnas.map(() => '1');
        }
        if (estilos.colWidths.length < comp.columnas.length) {
          for (let i = estilos.colWidths.length; i < comp.columnas.length; i++) {
            estilos.colWidths[i] = '1';
          }
        } else if (estilos.colWidths.length > comp.columnas.length) {
          estilos.colWidths = estilos.colWidths.slice(0, comp.columnas.length);
        }
        
        // Bordes por columna
        if (!Array.isArray(estilos.colBorderStyles)) estilos.colBorderStyles = [];
        if (!Array.isArray(estilos.colBorderWidths)) estilos.colBorderWidths = [];
        if (!Array.isArray(estilos.colBorderColors)) estilos.colBorderColors = [];
        
        const ensureLen = (arr: any[], fill: any) => {
          if (arr.length < comp.columnas.length) {
            for (let i = arr.length; i < comp.columnas.length; i++) arr[i] = fill;
          } else if (arr.length > comp.columnas.length) {
            arr.splice(comp.columnas.length);
          }
        };
        
        ensureLen(estilos.colBorderStyles, 'none');
        ensureLen(estilos.colBorderWidths, '1px');
        ensureLen(estilos.colBorderColors, '#cccccc');
        
        comp.columnas.forEach(col => col.forEach(visit));
      }
      
      // Asegurar fontFamily en componentes con texto
      if (['titulo', 'texto', 'boton', 'contador', 'perfil', 'banner', 'descuento', 'info-producto'].includes(comp.tipo as string)) {
        if (!comp.estilos.fontFamily) {
          comp.estilos.fontFamily = this.settings.fontFamily;
        }
      }
    };
    
    this.componentesCanvas.forEach(visit);
    
    if (!this.settings.moduleSpacing) this.settings.moduleSpacing = '30px';
    if (!this.settings.fontFamily) this.settings.fontFamily = "'Work Sans', sans-serif";
  }

  // ================= Serialización a payload backend =================
  private fontSizeToToken(fontSize?: string): string | undefined {
    if (!fontSize) return undefined;
    const num = parseInt(fontSize, 10);
    if (num >= 32) return 'xl';
    if (num >= 24) return 'lg';
    if (num >= 18) return 'md';
    if (num >= 14) return 'sm';
    return 'xs';
  }

  private serializeComponent(comp: ComponenteBuilder): any {
    // Helper para serializar bordes solo si hay estilo diferente a 'none'
    const getBorder = (estilos: any) => {
      if (estilos.borderStyle && estilos.borderStyle !== 'none') {
        return {
          style: estilos.borderStyle,
          width: estilos.borderWidth || '1px',
          color: estilos.borderColor || '#cccccc'
        };
      }
      return null;
    };

    switch (comp.tipo) {
      case 'titulo':
        return {
          id: comp.id,
          tipo: 'titulo',
          props: {
            texto: comp.contenido.texto,
            align: comp.estilos.textAlign || 'left',
            color: comp.estilos.color || '#000000',
            size: this.fontSizeToToken(comp.estilos.fontSize),
            fontFamily: comp.estilos.fontFamily,
            border: getBorder(comp.estilos)
          }
        };
      case 'texto':
        return {
          id: comp.id,
          tipo: 'texto',
          props: {
            content: comp.contenido.texto,
            align: comp.estilos.textAlign || 'left',
            color: comp.estilos.color || '#333333',
            size: this.fontSizeToToken(comp.estilos.fontSize),
            fontFamily: comp.estilos.fontFamily,
            border: getBorder(comp.estilos)
          }
        };
      case 'imagen':
        return {
          id: comp.id,
            tipo: 'imagen',
            props: {
              src: comp.contenido._uploadedPath || comp.contenido.url,
              alt: comp.contenido.alt || '',
              align: comp.estilos.textAlign || 'center',
              width: comp.estilos.width,
              maxWidth: comp.estilos.maxWidth,
              fontFamily: comp.estilos.fontFamily,
              border: getBorder(comp.estilos)
            }
        };
      case 'redes':
        return {
          id: comp.id,
          tipo: 'redes',
          props: {
            facebook: comp.contenido.facebook || null,
            twitter: comp.contenido.twitter || null,
            instagram: comp.contenido.instagram || null,
            linkedin: comp.contenido.linkedin || null,
            youtube: comp.contenido.youtube || null,
            tiktok: comp.contenido.tiktok || null,
            border: getBorder(comp.estilos)
          }
        };
      case 'boton':
        return {
          id: comp.id,
          tipo: 'boton',
          props: {
            texto: comp.contenido.texto,
            url: comp.contenido.url,
            backgroundColor: comp.estilos.backgroundColor,
            color: comp.estilos.textColor || comp.estilos.color,
            fontFamily: comp.estilos.fontFamily,
            padding: comp.estilos.padding,
            borderRadius: comp.estilos.borderRadius,
            border: getBorder(comp.estilos)
          }
        };
      case 'espaciador':
        return {
          id: comp.id,
          tipo: 'espaciador',
          props: {
            height: comp.estilos.height || '40px',
            border: getBorder(comp.estilos)
          }
        };
      case 'contador':
        return {
          id: comp.id,
          tipo: 'contador',
          props: {
            targetDate: comp.contenido.targetDate,
            backgroundColor: comp.estilos.backgroundColor || 'transparent',
            padding: comp.estilos.padding || '10px',
            borderRadius: comp.estilos.borderRadius || '5px',
            color: comp.estilos.color,
            size: this.fontSizeToToken(comp.estilos.fontSize),
            align: comp.estilos.textAlign || 'center',
            fontFamily: comp.estilos.fontFamily,
            border: getBorder(comp.estilos)
          }
        };
      case 'perfil':
        return {
          id: comp.id,
          tipo: 'perfil',
          props: {
            nombre: comp.contenido.nombre,
            rol: comp.contenido.rol,
            bio: comp.contenido.bio,
            avatarUrl: comp.contenido._avatarUploadedPath || comp.contenido.avatarUrl || null,
            align: comp.estilos.textAlign || 'center',
            color: comp.estilos.color || '#2c3e50',
            fontFamily: comp.estilos.fontFamily,
            border: getBorder(comp.estilos)
          }
        };
      case 'fila':
        const numCols = comp.columnas?.length || 2;
        const colWidth = Math.floor(12 / numCols); // distribución simple
        return {
          id: comp.id,
          tipo: 'fila',
          props: {
            cols: (comp.columnas || []).map((col, idx) => ({
              width: (comp as any).estilos?.colWidths?.[idx] || colWidth,
              bgColor: (comp as any).estilos?.colBgColors?.[idx] || null,
              children: col.map(child => this.serializeComponent(child))
            }))
          }
        };
      case 'banner':
        return {
          id: comp.id,
          tipo: 'banner',
          props: {
            titulo: comp.contenido.titulo,
            subtitulo: comp.contenido.subtitulo,
            imagenFondo: comp.contenido._uploadedPath || comp.contenido.imagenFondo,
            textoBoton: comp.contenido.textoBoton,
            urlBoton: comp.contenido.urlBoton,
            overlayColor: comp.estilos.overlayColor,
            colorTitulo: comp.estilos.colorTitulo,
            colorSubtitulo: comp.estilos.colorSubtitulo,
            align: comp.estilos.textAlign || 'center',
            padding: comp.estilos.padding,
            fontFamily: comp.estilos.fontFamily,
            border: getBorder(comp.estilos)
          }
        };

      case 'descuento':
        const porcentaje = this.calcularPorcentajeDescuento(
          comp.contenido.precioOriginal,
          comp.contenido.precioDescuento
        );
        
        return {
          id: comp.id,
          tipo: 'descuento',
          props: {
            precioOriginal: comp.contenido.precioOriginal,
            precioDescuento: comp.contenido.precioDescuento,
            moneda: comp.contenido.moneda || 'MXN',
            porcentajeDescuento: porcentaje,
            textoDescuento: comp.contenido.textoDescuento,
            mostrarContador: comp.contenido.mostrarContador,
            targetDate: comp.contenido.mostrarContador ? comp.contenido.targetDate : null,
            backgroundColor: comp.estilos.backgroundColor,
            colorTexto: comp.estilos.colorTexto,
            colorPrecioOriginal: comp.estilos.colorPrecioOriginal,
            colorPrecioDescuento: comp.estilos.colorPrecioDescuento,
            padding: comp.estilos.padding,
            borderRadius: comp.estilos.borderRadius,
            align: comp.estilos.textAlign || 'center',
            fontFamily: comp.estilos.fontFamily,
            border: getBorder(comp.estilos)
          }
        };

      default:
        return { id: comp.id, tipo: comp.tipo, props: { raw: comp.contenido } };
    }
  }

  toBackendPayload(version: number = 1): any {
    return {
      version,
      settings: {
        backgroundColor: this.settings.backgroundColor,
        profileHydration: { local: true, api: true }
      },
      layout: this.componentesCanvas.map(c => this.serializeComponent(c))
    };
  }

  guardarEnBackend(clienteId: number): void {
    const payload = this.toBackendPayload();
    this.landingsService.saveLandingPage(clienteId, payload).subscribe({
      next: () => {},
      error: () => {}
    });
  }

  // Guardar builder (upsert) usando POST /landing-page/builder/:productId
  guardarBuilder(productId: number): void {
    if (productId == null) return;
    const builder = this.toBackendPayload();
    this.isGuardandoBuilder = true;
    this.landingsService.saveLandingBuilder(productId, builder).subscribe({
      next: () => {
        this.isGuardandoBuilder = false;
      },
      error: () => {
        this.isGuardandoBuilder = false;
      }
    });
  }

  // Hidratar y guardar (PUT /landing-page/builder/:productId/hydrate)
  hidratarYGuardarBuilder(productId: number, usarPayloadLocal: boolean = true): void {
    if (productId == null) return;
    const body = usarPayloadLocal ? this.toBackendPayload() : undefined;
    this.landingsService.hydrateLandingBuilder(productId, body).subscribe({
      next: () => {},
      error: () => {}
    });
  }

  finalizarEdicionYGuardar(): void {
    if (this.landingPageId == null && this.productId == null) {
      this.alerta('No se puede guardar sin un identificador válido.');
      return;
    }
    const uploading = this.tieneCargasPendientes();
    if (uploading) {
      this.alerta('Aún hay imágenes subiendo. Espera a que finalicen antes de publicar.');
      return;
    }

    const payload = {
      componentes: this.componentesCanvas,
      settings: this.settings,
      plantilla: 'Landing4',
    };

    this.isGuardandoBuilder = true;

    if (this.landingPageId != null) {
      this.landingPagesService.saveBuilder(this.landingPageId, payload).subscribe({
        next: () => {
          this.isGuardandoBuilder = false;
          this.modoEdicion = false;
          this.mostrarPanelPropiedades = false;
          this.componenteSeleccionado = null;
          this.guardarConfiguracion();
          this.alerta('Landing page guardada exitosamente');
        },
        error: (error: any) => {
          this.isGuardandoBuilder = false;
          if (error.status === 413) {
            this.alerta('El contenido es demasiado grande. Intenta reducir el tamaño de las imágenes.');
          } else {
            this.alerta('Error al guardar la landing page. Inténtalo nuevamente.');
          }
        },
      });
      return;
    }

    this.landingsService.saveLandingPage4(this.productId!, payload).subscribe({
      next: (response: any) => {
        this.isGuardandoBuilder = false;
        this.modoEdicion = false;
        this.mostrarPanelPropiedades = false;
        this.componenteSeleccionado = null;
        this.guardarConfiguracion();
        this.alerta(response.message || 'Landing page guardada exitosamente');
      },
      error: (error: any) => {
        this.isGuardandoBuilder = false;
        if (error.status === 404) {
          this.alerta('Producto no encontrado. Verifica el ID del producto.');
        } else if (error.status === 400) {
          this.alerta(error.error?.message || 'Datos inválidos en la configuración');
        } else if (error.status === 413) {
          this.alerta('El contenido es demasiado grande. Intenta reducir el tamaño de las imágenes.');
        } else {
          this.alerta('Error al guardar la landing page. Inténtalo nuevamente.');
        }
      },
    });
  }

  private procederGuardado(payload: any): void {
    this.guardarConfiguracion();
    this.isGuardandoBuilder = true;
    this.landingsService.saveLandingBuilder(this.productId as number, payload).subscribe({
      next: () => {
        this.isGuardandoBuilder = false;
        this.modoEdicion = false;
        this.mostrarPanelPropiedades = false;
        this.componenteSeleccionado = null;
      },
      error: () => {
        this.isGuardandoBuilder = false;
      }
    });
  }

  private tieneCargasPendientes(): boolean {
    const visit = (comp: ComponenteBuilder): boolean => {
      if ((comp as any)?.contenido?.uploading || (comp as any)?.contenido?.avatarUploading) return true;
      if (comp.tipo === 'fila' && comp.columnas) {
        for (const col of comp.columnas) {
          for (const child of col) {
            if (visit(child)) return true;
          }
        }
      }
      return false;
    };
    return this.componentesCanvas.some(visit);
  }

  private rutasImagenesInvalidas(payload: any): string[] {
    const invalid: string[] = [];
    const isBad = (v: any) => typeof v === 'string' && /^(data:|blob:)/i.test(v);
    const walk = (node: any, path: string) => {
      if (!node || typeof node !== 'object') return;
      if (Array.isArray(node)) {
        node.forEach((item, i) => walk(item, `${path}[${i}]`));
        return;
      }
      // detectar en props
      if (node.props && typeof node.props === 'object') {
        if (isBad(node.props.src)) invalid.push(`${path}.props.src`);
        if (isBad(node.props.avatarUrl)) invalid.push(`${path}.props.avatarUrl`);
        // fila: navegar por cols->children
        if (node.tipo === 'fila' && Array.isArray(node.props.cols)) {
          node.props.cols.forEach((c: any, i: number) => {
            if (Array.isArray(c.children)) {
              c.children.forEach((child: any, j: number) => walk(child, `${path}.props.cols[${i}].children[${j}]`));
            }
          });
        }
      }
    };
    if (payload && Array.isArray(payload.layout)) {
      payload.layout.forEach((item: any, idx: number) => walk(item, `layout[${idx}]`));
    }
    return invalid;
  }

  private async repararImagenesEmbebidas(): Promise<void> {
    const tasks: Promise<void>[] = [];
    const visit = (comp: ComponenteBuilder) => {
      const fixField = (field: 'url' | 'avatarUrl', filenameFallback: string) => {
        const val = (comp as any)?.contenido?.[field];
        if (typeof val === 'string' && /^(data:|blob:)/i.test(val)) {
          const p = this.subirDesdeEmbebido(val, filenameFallback)
            .then((newUrl) => {
              if (newUrl) {
                (comp as any).contenido[field] = newUrl;
              }
            })
            .catch(() => {});
          tasks.push(p);
        }
      };
      if (comp.tipo === 'imagen') fixField('url', (comp as any)?.contenido?.fileName || 'imagen.png');
      if (comp.tipo === 'perfil') fixField('avatarUrl', (comp as any)?.contenido?.avatarName || 'avatar.png');
      if (comp.tipo === 'fila' && comp.columnas) comp.columnas.forEach(col => col.forEach(visit));
    };
    this.componentesCanvas.forEach(visit);
    if (tasks.length === 0) return;
    await Promise.all(tasks);
    this.guardarConfiguracion();
  }

  private async subirDesdeEmbebido(src: string, filename: string): Promise<string | null> {
    try {
      let blob: Blob;
      if (/^data:/i.test(src)) {
        blob = this.dataUrlToBlob(src);
      } else if (/^blob:/i.test(src)) {
        const res = await fetch(src);
        blob = await res.blob();
      } else {
        return null;
      }
      const form = new FormData();
      const file = new File([blob], filename, { type: blob.type || 'application/octet-stream' });
      form.append('file', file, filename);
      const resp = await lastValueFrom(this.landingsService.uploadImg(form));
      // Backend retorna path, lo retornamos tal cual
      const path = resp?.path || resp?.filename;
      return path || null;
    } catch {
      return null;
    }
  }

  private dataUrlToBlob(dataUrl: string): Blob {
    const arr = dataUrl.split(',');
    const mimeMatch = /data:([^;]+);base64/.exec(arr[0] || '') || [];
    const mime = mimeMatch[1] || 'application/octet-stream';
    const bstr = atob(arr[1] || '');
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new Blob([u8arr], { type: mime });
  }
}
